/**
 * Real-Time SSE Client
 * Connects to /api/events for server-sent events
 * Handles auto-reconnection and event dispatching
 */

import { getApiKey } from './auth'

// API base URL from vite config
const API_URL = __API_URL__

/**
 * Event types from backend (must match api/realtime.py EventType)
 */
export const EventType = {
  // Task Events
  TASK_CREATED: 'task_created',
  TASK_CLAIMED: 'task_claimed',
  TASK_COMPLETED: 'task_completed',
  TASK_UNCLAIMED: 'task_unclaimed',
  TASK_UPDATED: 'task_updated',
  TASK_DELETED: 'task_deleted',

  // Session Events
  SESSION_CREATED: 'session_created',
  SESSION_UPDATED: 'session_updated',
  SESSION_CHECKIN: 'session_checkin',
  SESSION_RESPONSE: 'session_response',

  // Session Task Definition Events
  SESSION_TASK_DEF_CREATED: 'session_task_def_created',
  SESSION_TASK_DEF_UPDATED: 'session_task_def_updated',
  SESSION_TASK_DEF_DELETED: 'session_task_def_deleted',
  SESSION_TASK_DEF_REORDERED: 'session_task_def_reordered',

  // Client Events
  CLIENT_CREATED: 'client_created',
  CLIENT_UPDATED: 'client_updated',
  CONTACT_LOGGED: 'contact_logged',
  CLIENT_STATUS_CHANGED: 'client_status_changed',

  // Campaign Events
  CAMPAIGN_CREATED: 'campaign_created',
  CAMPAIGN_UPDATED: 'campaign_updated',
  CAMPAIGN_PROGRESS: 'campaign_progress',
  CAMPAIGN_COMPLETED: 'campaign_completed',

  // Loan Events
  LOAN_CREATED: 'loan_created',
  LOAN_PAYMENT: 'loan_payment',
  LOAN_COLLECTION_LOGGED: 'loan_collection_logged',
  LOAN_CHOPPED: 'loan_chopped',

  // Event (Poker Events) Events
  EVENT_CREATED: 'event_created',
  EVENT_UPDATED: 'event_updated',
  EVENT_TASK_CLAIMED: 'event_task_claimed',
  EVENT_TASK_COMPLETED: 'event_task_completed',
}

/**
 * Connection states
 */
export const ConnectionState = {
  DISCONNECTED: 'disconnected',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  ERROR: 'error',
}

/**
 * SSE Client Manager
 * Handles connection, auto-reconnect, and event dispatching
 */
class RealtimeClient {
  constructor() {
    this.eventSource = null
    this.state = ConnectionState.DISCONNECTED
    this.listeners = new Map() // eventType -> Set of callbacks
    this.stateListeners = new Set() // connection state callbacks
    this.reconnectAttempts = 0
    this.maxReconnectAttempts = 10
    this.reconnectDelay = 1000 // Start with 1 second
    this.reconnectTimer = null
  }

  /**
   * Connect to SSE endpoint
   */
  connect() {
    const apiKey = getApiKey()
    if (!apiKey) {
      this.setState(ConnectionState.DISCONNECTED)
      return
    }

    // Don't reconnect if already connected or connecting
    if (this.eventSource && this.state === ConnectionState.CONNECTED) {
      return
    }

    this.setState(ConnectionState.CONNECTING)

    // SSE endpoint uses query param for auth (EventSource doesn't support headers)
    const url = `${API_URL}/events?api_key=${encodeURIComponent(apiKey)}`

    try {
      this.eventSource = new EventSource(url)

      this.eventSource.onopen = () => {
        this.setState(ConnectionState.CONNECTED)
        this.reconnectAttempts = 0
        this.reconnectDelay = 1000
      }

      this.eventSource.onerror = (error) => {
        console.error('SSE connection error:', error)

        // Close the connection
        this.eventSource?.close()
        this.eventSource = null

        // Check if we should reconnect
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
          this.setState(ConnectionState.CONNECTING)
          this.scheduleReconnect()
        } else {
          this.setState(ConnectionState.ERROR)
        }
      }

      this.eventSource.onmessage = (event) => {
        this.handleMessage(event)
      }
    } catch (error) {
      console.error('Failed to create EventSource:', error)
      this.setState(ConnectionState.ERROR)
    }
  }

  /**
   * Disconnect from SSE
   */
  disconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }

    if (this.eventSource) {
      this.eventSource.close()
      this.eventSource = null
    }

    this.setState(ConnectionState.DISCONNECTED)
    this.reconnectAttempts = 0
  }

  /**
   * Schedule a reconnection attempt with exponential backoff
   */
  scheduleReconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
    }

    this.reconnectTimer = setTimeout(() => {
      this.reconnectAttempts++
      this.reconnectDelay = Math.min(this.reconnectDelay * 2, 30000) // Max 30 seconds
      this.connect()
    }, this.reconnectDelay)
  }

  /**
   * Handle incoming SSE message
   */
  handleMessage(event) {
    try {
      const data = JSON.parse(event.data)
      const { event_type, payload } = data

      // Dispatch to listeners
      const callbacks = this.listeners.get(event_type)
      if (callbacks) {
        callbacks.forEach((callback) => {
          try {
            callback(payload)
          } catch (err) {
            console.error(`Error in SSE event handler for ${event_type}:`, err)
          }
        })
      }

      // Also dispatch to wildcard listeners
      const wildcardCallbacks = this.listeners.get('*')
      if (wildcardCallbacks) {
        wildcardCallbacks.forEach((callback) => {
          try {
            callback(event_type, payload)
          } catch (err) {
            console.error('Error in SSE wildcard handler:', err)
          }
        })
      }
    } catch (error) {
      console.error('Failed to parse SSE message:', error, event.data)
    }
  }

  /**
   * Subscribe to an event type
   * @param {string} eventType - Event type to listen for (or '*' for all)
   * @param {Function} callback - Callback function
   * @returns {Function} Unsubscribe function
   */
  subscribe(eventType, callback) {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set())
    }
    this.listeners.get(eventType).add(callback)

    // Return unsubscribe function
    return () => {
      const callbacks = this.listeners.get(eventType)
      if (callbacks) {
        callbacks.delete(callback)
        if (callbacks.size === 0) {
          this.listeners.delete(eventType)
        }
      }
    }
  }

  /**
   * Subscribe to connection state changes
   * @param {Function} callback - Callback receiving connection state
   * @returns {Function} Unsubscribe function
   */
  onStateChange(callback) {
    this.stateListeners.add(callback)
    // Immediately call with current state
    callback(this.state)

    return () => {
      this.stateListeners.delete(callback)
    }
  }

  /**
   * Update connection state and notify listeners
   */
  setState(newState) {
    if (this.state !== newState) {
      this.state = newState
      this.stateListeners.forEach((callback) => {
        try {
          callback(newState)
        } catch (err) {
          console.error('Error in state change listener:', err)
        }
      })
    }
  }

  /**
   * Get current connection state
   */
  getState() {
    return this.state
  }

  /**
   * Check if connected
   */
  isConnected() {
    return this.state === ConnectionState.CONNECTED
  }
}

// Singleton instance
export const realtimeClient = new RealtimeClient()

// Export for convenience
export default realtimeClient
